import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import {
  Button,
  Dropdown,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
} from "@fluentui/react-components";
import { ArrowClockwise20Regular } from "@fluentui/react-icons";

import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import { useSelectedMicrophoneVolume } from "./use-selected-microphone-volume.js";
import { useSettingsAudioDevices } from "./use-settings-audio-devices.js";
import { useSpeakerTestSound } from "./use-speaker-test-sound.js";

const VOLUME_PERCENT_SCALE = 100;
const MIN_VOLUME_LEVEL = 0;
const MAX_VOLUME_LEVEL = 1;
const VOLUME_METER_BACKGROUND =
  "linear-gradient(90deg, #2B8C43 0%, #2B8C43 33.333%, #F07F18 66.667%, #CF3435 100%)";

const clampVolumeLevel = (volumeLevel: number) => {
  if (Number.isNaN(volumeLevel)) {
    return MIN_VOLUME_LEVEL;
  }

  if (volumeLevel < MIN_VOLUME_LEVEL) {
    return MIN_VOLUME_LEVEL;
  }

  if (volumeLevel > MAX_VOLUME_LEVEL) {
    return MAX_VOLUME_LEVEL;
  }

  return volumeLevel;
};

export const SettingsUserAudioDevices = () => {
  const {
    microphones,
    speakers,
    selectedMicrophoneId,
    selectedSpeakerId,
    isApplying,
    error,
    setPreferredMicrophone,
    setPreferredSpeaker,
    refresh,
  } = useSettingsAudioDevices();

  const { isPlaying, togglePlay, sinkError } =
    useSpeakerTestSound(selectedSpeakerId);

  const { volumeLevel, permissionDenied } =
    useSelectedMicrophoneVolume(selectedMicrophoneId);
  const normalizedVolumeLevel = clampVolumeLevel(volumeLevel);
  const volumePercent = Math.round(
    normalizedVolumeLevel * VOLUME_PERCENT_SCALE
  );
  const hiddenFillPercent = (
    (MAX_VOLUME_LEVEL - normalizedVolumeLevel) *
    VOLUME_PERCENT_SCALE
  ).toFixed(1);

  const selectedMicrophone = microphones.find(
    (device) => device.deviceId === selectedMicrophoneId
  );
  const selectedSpeaker = speakers.find(
    (device) => device.deviceId === selectedSpeakerId
  );

  const handleRefreshClick = useStableCallback(() => {
    refresh().catch((refreshError) => {
      recordErrorInSentry(refreshError);
    });
  });
  const handleOptionSelect263 = useStableCallback((_event, data) => {
    if (data.optionValue) {
      setPreferredSpeaker(data.optionValue);
    }
  });

  const handleOptionSelect198 = useStableCallback((_event, data) => {
    if (data.optionValue) {
      setPreferredMicrophone(data.optionValue);
    }
  });

  return (
    <div className="flex flex-col gap-4">
      {Boolean(error) && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {Boolean(isApplying) && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-3">
          <Spinner size="tiny" />
          <Text size={300}>Applying device change...</Text>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Text size={300} weight="semibold">
          Microphone
        </Text>

        <div className="flex items-start gap-2">
          <div className="flex w-full flex-col gap-2">
            <Dropdown
              className="w-full"
              disabled={isApplying || microphones.length === 0}
              onOptionSelect={(_event, data) => {
                if (data.optionValue) {
                  setPreferredMicrophone(data.optionValue);
                }
              }}
              placeholder={
                microphones.length > 0
                  ? "Select microphone"
                  : "No microphone detected"
              }
              selectedOptions={
                selectedMicrophone ? [selectedMicrophone.deviceId] : []
              }
              value={selectedMicrophone ? selectedMicrophone.label : ""}
            >
              {microphones.map((device) => (
                <Option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </Option>
              ))}
            </Dropdown>

            <div
              aria-label="Microphone volume level"
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={volumePercent}
              className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
            >
              <div
                className="h-full w-full transition-all duration-75"
                style={{
                  background: VOLUME_METER_BACKGROUND,
                  clipPath: `inset(0 ${hiddenFillPercent}% 0 0)`,
                }}
              />
            </div>
          </div>

          <Button
            appearance="subtle"
            aria-label="Refresh microphone list"
            icon={<ArrowClockwise20Regular />}
            onClick={handleRefreshClick}
          />
        </div>

        {Boolean(permissionDenied) && (
          <Text className="text-red-600" size={200}>
            Microphone access denied. Please grant permission to see the volume
            level.
          </Text>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Text size={300} weight="semibold">
          Speaker
        </Text>

        <div className="flex items-start gap-2">
          <Dropdown
            className="w-full"
            disabled={isApplying || speakers.length === 0}
            onOptionSelect={(_event, data) => {
              if (data.optionValue) {
                setPreferredSpeaker(data.optionValue);
              }
            }}
            placeholder={
              speakers.length > 0 ? "Select speaker" : "No speaker detected"
            }
            selectedOptions={selectedSpeaker ? [selectedSpeaker.deviceId] : []}
            value={selectedSpeaker ? selectedSpeaker.label : ""}
          >
            {speakers.map((device) => (
              <Option key={device.deviceId} value={device.deviceId}>
                {device.label}
              </Option>
            ))}
          </Dropdown>

          <Button
            appearance="subtle"
            aria-label="Refresh speaker list"
            icon={<ArrowClockwise20Regular />}
            onClick={handleRefreshClick}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button appearance="primary" onClick={togglePlay}>
            {isPlaying ? "Stop" : "Test"}
          </Button>

          {Boolean(sinkError) && (
            <Text className="text-red-600" size={200}>
              {sinkError}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};
